<template>
  <div class="routes-map">
    <GmapMap
      ref="mapRef"
      class="routes-map__map"
      map-type-id="roadmap"
      :center="center"
      :zoom="20"
      :options="mapOptions"
    >
      <template v-for="route in routes">
        <GmapPolyline
          v-if="showedRoutes[route.id] && route.polylines?.length"
          :key="`polyline-${route.id}`"
          :path="route.polylines"
          :options="{
            strokeColor: route.color,
            strokeOpacity: '1.0',
            strokeWeight: '3',
          }"
        />

        <template v-for="point in route.timelines">
          <GmapCustomMarker
            v-if="showedRoutes[route.id] && shouldRenderPoint(route.timelines, point)"
            :key="`marker-${point.point_id}`"
            :marker="{
              lat: point.address.geolocation.coordinate?.latitude,
              lng: point.address.geolocation.coordinate?.longitude,
            }"
          >
            <PointMarker
              v-if="!['END', 'IDLE', 'BREAK'].includes(point.type)"
              :id="`map-event-item-${point.point_id}`"
              :point="point"
              :number="getPointIndex(route.timelines, point)"
              :color="route.color"
              :isRoSwatActive="isRoSwatActive"
              :isStartPoint="isStartPickupDefaultPoint(route.timelines, point)"
            />
            <PopoverItem type="map" :point="point" />
          </GmapCustomMarker>
        </template>
      </template>
    </GmapMap>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import GmapCustomMarker from 'vue2-gmap-custom-marker'
import { Helper } from '@/mixin/helper'

import PointMarker from '../components/PointMarker'
import PopoverItem from '../components/PopoverItem'

import { mapGetters, mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'

export default {
  mixins: [Helper],
  components: {
    GmapCustomMarker,
    PointMarker,
    PopoverItem,
  },
  props: {
    vehicles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      map: null,

      routes: [],
      center: { lat: 0, lng: 0 }, // Center of the map, can be dynamic
      mapOptions: {
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false,
      },
    }
  },
  computed: {
    ...mapState('routeOptimization', ['showedRoutes']),
    ...mapGetters({
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    google: gmapApi,
    isRoSwatActive() {
      const isSamator =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false

      return isSamator && isActive
    },
    points() {
      const points = []
      this.routes.forEach((route) => {
        route.timelines.forEach((point) => {
          if (!this.shouldRenderPoint(route.timelines, point)) return
          if (point.type === 'START' || ['END', 'IDLE', 'BREAK'].includes(point.type)) return

          if (
            point.address.geolocation.coordinate.latitude != null &&
            point.address.geolocation.coordinate.longitude != null
          ) {
            points.push({
              routeId: route.id,
              marker: {
                lat: point.address.geolocation.coordinate.latitude,
                lng: point.address.geolocation.coordinate.longitude,
              },
            })
          }
        })
      })

      return points
    },
  },
  async mounted() {
    await this.initMap()
  },
  methods: {
    getReferenceCoordinate(timelines = []) {
      const pointWithCoordinate = timelines.find((point) => {
        const latitude = point.address?.geolocation?.coordinate?.latitude
        const longitude = point.address?.geolocation?.coordinate?.longitude

        return latitude != null && longitude != null
      })

      if (!pointWithCoordinate) return null

      return {
        lat: Number(pointWithCoordinate.address.geolocation.coordinate.latitude),
        lng: Number(pointWithCoordinate.address.geolocation.coordinate.longitude),
      }
    },

    getCoordinateDistance(firstCoordinate, secondCoordinate) {
      if (!firstCoordinate || !secondCoordinate) return Number.POSITIVE_INFINITY

      return Math.hypot(
        Number(firstCoordinate.lat) - Number(secondCoordinate.lat),
        Number(firstCoordinate.lng) - Number(secondCoordinate.lng)
      )
    },

    normalizePolyline(polyline) {
      if (!polyline) return []

      if (typeof polyline === 'string') {
        return this.decodePolyline(polyline)
      }

      if (Array.isArray(polyline)) {
        return polyline
          .map((point) => {
            if (point?.lat != null && point?.lng != null) {
              return {
                lat: Number(point.lat),
                lng: Number(point.lng),
              }
            }

            return null
          })
          .filter(Boolean)
      }

      return []
    },

    decodePolyline(encoded, precision = 1e-5) {
      let index = 0,
        len = encoded.length
      let lat = 0,
        lng = 0
      const coordinates = []

      while (index < len) {
        let b,
          shift = 0,
          result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        const deltaLat = result & 1 ? ~(result >> 1) : result >> 1
        lat += deltaLat

        shift = 0
        result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        const deltaLng = result & 1 ? ~(result >> 1) : result >> 1
        lng += deltaLng

        coordinates.push({
          lat: lat * precision,
          lng: lng * precision,
        })
      }

      return coordinates
    },

    getCenterCoordinates(coords) {
      // Initialize the sum of latitudes and longitudes
      let totalLat = 0
      let totalLng = 0

      // Iterate over the array to sum up the latitudes and longitudes
      for (let i = 0; i < coords.length; i++) {
        totalLat += coords[i].marker.lat
        totalLng += coords[i].marker.lng
      }

      // Calculate the average latitude and longitude
      const centerLat = totalLat / coords.length
      const centerLng = totalLng / coords.length

      // Return the center coordinates
      return {
        lat: centerLat,
        lng: centerLng,
      }
    },

    // To calculate the bounds of the map based on all points
    calculateBounds() {
      const bounds = new this.google.maps.LatLngBounds()
      this.points.forEach((point) => {
        const position = new this.google.maps.LatLng(point.marker.lat, point.marker.lng)
        bounds.extend(position)
      })
      return bounds
    },

    async initMap() {
      const mapRef = this.$refs.mapRef
      await mapRef.$mapPromise

      // Create routes from vehicles
      this.vehicles.forEach((item) => {
        const route = {
          id: item.id,
          color: item.color,
        }

        const referenceCoordinate = this.getReferenceCoordinate(item.timelines)

        // decode the polyline
        const decodedPolylines = (item.polylines || []).flatMap((polyline) => {
          if (typeof polyline !== 'string') {
            return this.normalizePolyline(polyline)
          }

          const decodedPolyline5 = this.decodePolyline(polyline, 1e-5)
          const decodedPolyline6 = this.decodePolyline(polyline, 1e-6)

          if (!referenceCoordinate) {
            return decodedPolyline5
          }

          const distancePolyline5 = this.getCoordinateDistance(
            decodedPolyline5[0],
            referenceCoordinate
          )
          const distancePolyline6 = this.getCoordinateDistance(
            decodedPolyline6[0],
            referenceCoordinate
          )

          return distancePolyline5 <= distancePolyline6 ? decodedPolyline5 : decodedPolyline6
        })

        route.polylines = decodedPolylines
        route.timelines = cloneDeep(item.timelines)

        this.routes.push(route)
      })

      await this.$nextTick()

      // get the center of map and fit the bounds
      if (this.points.length > 0) {
        this.center = this.getCenterCoordinates(this.points)
        const bounds = this.calculateBounds()

        mapRef.fitBounds(bounds)
      }
    },

    getStartPickupDefaultPointId(timelines) {
      if (!this.isRoSwatActive) return null

      const startPoint = timelines.find((point) => point.type === 'START')
      const pickupDefaultPoint = timelines.find((point) => {
        if (point.type !== 'DEFAULT') return false

        const hasPickupActivity = point.activities?.some((activity) => activity.type === 'PICKUP')
        if (!hasPickupActivity) return false

        const startAddressId = startPoint?.address?.id
        const pointAddressId = point.address?.id

        if (startAddressId != null && pointAddressId != null) {
          return startAddressId === pointAddressId
        }

        return startPoint?.address?.name === point.address?.name
      })

      if (!startPoint || !pickupDefaultPoint) return null

      return pickupDefaultPoint.point_id
    },

    getStartPointId(timelines) {
      if (!this.isRoSwatActive) return null

      return timelines.find((point) => point.type === 'START')?.point_id || null
    },

    isStartPickupDefaultPoint(timelines, point) {
      return point.point_id === this.getStartPickupDefaultPointId(timelines)
    },

    shouldRenderPoint(timelines, point) {
      return point.point_id !== this.getStartPointId(timelines)
    },

    getPointIndex(timelines, currentPoint) {
      // Jika point adalah BREAK atau END, return null (tidak perlu index)
      if (
        ['BREAK', 'IDLE', 'END'].includes(currentPoint.type) ||
        this.isStartPickupDefaultPoint(timelines, currentPoint)
      ) {
        return null
      }

      const hiddenPointId = this.getStartPointId(timelines)

      // Hitung index berdasarkan urutan point DEFAULT dengan service_time selain PT0S
      let index = 0
      for (const point of timelines) {
        if (point.point_id === hiddenPointId) continue
        if (this.isStartPickupDefaultPoint(timelines, point)) continue

        if (point.type === 'DEFAULT' && point.service_time !== 'PT0S') {
          index++
          if (point.point_id === currentPoint.point_id) {
            return index
          }
        }
      }
      return index
    },
  },
}
</script>

<style lang="scss" scoped>
.routes-map {
  height: 100%;
  width: 100%;

  &__map {
    height: calc(100vh - 300px);
    width: 100%;
  }
}
</style>
